import { useEffect, useRef, useState } from 'react'

import Meta from '@/components/meta'
import Footer from '@/components/footer'
import HelpHeader from '@/components/help-header'
import HelpNav from '@/components/help-nav'
import TocNav from '@/components/toc-nav'

import CloudStatus from '@/components/cloud-status'
import NavContainer from '@/components/nav-container'
import HelpUsefulLinks from '@/components/help/help-useful-links'
import HelpDownloadPDF from '@/components/help/help-download-pdf'
import HelpContactForm from '@/components/help/help-contact-form'
import Hamburger from '@/icons/bwi/hamburger.svg'
import { slugify } from '@/utils/strings'
import { cx } from '@/utils/strings'

type HelpParentProps = {
  children: React.ReactNode
  footerNav: any
  newsletterForm: any
  title: string
  seo: any
  outlineBody?: any
  helpNav: any
  helpNavInitial?: any
  searchInHeader?: boolean
  headerBanners?: any
}

export default function HelpParent({
  children,
  footerNav,
  newsletterForm,
  title,
  seo,
  outlineBody,
  helpNav,
  helpNavInitial = [],
  searchInHeader = false,
  headerBanners = [],
}: HelpParentProps) {
  const headerRef = useRef(null)
  const [isNavOpen, setNavOpen] = useState(false)

  const [helpContactFormOpen, setHelpContactFormOpen] = useState(false)
  const supportRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const { hash } = window.location
    if (hash === '#contact-support') {
      setHelpContactFormOpen(true)
    }
  }, [])

  useEffect(() => {
    if (helpContactFormOpen)
      supportRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      })
  }, [helpContactFormOpen, supportRef.current])

  return (
    <>
      <Meta seo={seo} />
      <section className="pb-10 lg:pb-20">
        <HelpHeader
          ref={headerRef}
          hasLeftLinks={false}
          hasSearch={searchInHeader}
          twRoot={`bg-indigoBlue dark:bg-secondary-900`}
          outlineBody={outlineBody}
          headerBanners={headerBanners}
        />
        <div className="md:flex max-w-[1600px] m-auto px-5 xl:pr-0">
          <div className="flex-none lg:w-[235px] xl:w-[300px] lg:pt-5">
            <NavContainer
              backgroundColor="bg-secondary-50 dark:bg-secondary-900"
              position="left"
              isDrawerOpen={isNavOpen}
              onRequestClose={() => {
                setNavOpen(false)
              }}
              className="lg:sticky lg:rounded-2xl"
              headerRef={headerRef}
              drawerBreakpoint="lg"
            >
              <HelpNav {...{ helpNav, helpNavInitial }} />
            </NavContainer>
          </div>

          <div className="grow md:pr-5 lg:px-5 xl:px-10 relative overflow-hidden">
            <div className={cx('flex items-center pt-4', !outlineBody && 'place-content-end')}>
              <button onClick={() => setNavOpen(!isNavOpen)} id="openHelpMenu" className="w-9 h-9 mr-3 block lg:hidden">
                <Hamburger />
              </button>

              {helpNavInitial?.length > 0 ? (
                <div className="py-2 text-primaryBlue text-sm font-bold md:block grow dark:text-secondary-400">
                  {helpNavInitial
                    .map((nav: any) => nav.title)
                    .reduce((prev: any, curr: any) => [
                      prev,
                      <span key={slugify(prev)} className="font-bwi mx-[2px]">
                        &#xe91b;
                      </span>,
                      curr,
                    ])}
                </div>
              ) : (
                <div className="py-2 text-primaryBlue text-sm font-bold md:block grow block xl:hidden dark:text-secondary-400">
                  Help Center
                </div>
              )}
              <div className="hidden sm:block md:hidden lg:pt-2 lg:mb-2 xl:hidden">
                <div className="flex gap-4">
                  <div>
                    <HelpContactForm />
                  </div>
                  {outlineBody !== undefined && <HelpDownloadPDF />}
                </div>
              </div>
            </div>
            <div className="md:hidden">
              <TocNav sections={outlineBody} {...{ title, helpNavInitial }} />
            </div>
            <article className="">{children}</article>
          </div>
          {/* help right side bar */}
          <div className="hidden md:block md:flex-none w-[220px] py-5 xl:w-[280px] xl:p-5 xl:pl-0">
            <div className="md:flex md:flex-col md:gap-5">
              <HelpUsefulLinks />
              {outlineBody !== undefined && (
                <div>
                  <HelpDownloadPDF isButtonStyle />
                </div>
              )}
              <HelpContactForm isButtonStyle />
              <CloudStatus className="rounded-xl p-4 [h3]:text-lg [&*>div]:w-auto! [&*>div]:z-10 dark:bg-secondary-900 [&_h3]:text-base [&_h3]:font-bold [&_a]:text-sm xl:[&_a]:text-base" />
            </div>
            {outlineBody && (
              <div className="sticky top-20 max-h-[calc(100vh-8rem)] overflow-y-auto">
                <TocNav sections={outlineBody} {...{ title, helpNavInitial }} />
              </div>
            )}
          </div>
        </div>
      </section>
      <Footer footerNav={footerNav} newsletterForm={newsletterForm} />
    </>
  )
}
